import dayjs, { Dayjs } from "dayjs";
import { IFinancialInfo } from "../store/repsaApi";
import { IBrokerageAnniversaryModel, ITransactionFormData } from "../types/types";

type FinancialFormValues = Pick<
  ITransactionFormData,
  | "commissionPercentage"
  | "grossCommissionIncome"
  | "clientRebatePrice"
  | "clientRebateGCI"
  | "clientRebateFlatAmount"
  | "referralFeePaid"
  | "referralFeeReceived"
  | "coAgentSplit"
  | "coAgentSplit2"
  | "incentivesPaid"
  | "netCommissionIncome"
  | "netIncome"
  | "netIncomePercentage"
  | "brokerageSplit"
  | "additionalCharges"
  | "brkAnniversary"
  | "brkFlatFees"
  | "flatCommission"
  | "runningSplitTotal"
  | "runningAdditionalChargeTotal"
>;

const positiveOrZero = (value?: number) =>
  value && value > 0 ? value : 0;

/** Maps saved transaction financials to the financial fields of the transaction form */
export function mapFinancialsToFormValues(
  financials?: IFinancialInfo,
): FinancialFormValues {
  return {
    commissionPercentage: financials?.commissionPercentage ?? null,
    grossCommissionIncome: null,
    clientRebatePrice: financials?.clientRebatePercentage ?? null,
    clientRebateGCI: financials?.clientRebateGciPercentage ?? null,
    clientRebateFlatAmount: financials?.clientRebateFlatAmount ?? null,
    referralFeePaid: financials?.referralFeePaidPercentage ?? null,
    referralFeeReceived: financials?.referralFeeReceivedPercentage ?? null,
    coAgentSplit: financials?.coAgentSplitPaidPercentage ?? null,
    coAgentSplit2: financials?.coAgentSplit2PaidPercentage ?? null,
    incentivesPaid: financials?.incentivesAmount ?? null,
    netCommissionIncome: financials?.netCommissionIncome ?? null,
    netIncome: null,
    netIncomePercentage: null,
    brokerageSplit: positiveOrZero(financials?.brokerageSplitPercentage),
    additionalCharges: positiveOrZero(
      financials?.brokerageAdditionalChargePercentage,
    ),
    brkAnniversary: financials?.brokerageAnniversaryDate
      ? financials.brokerageAnniversaryDate
      : null,
    brkFlatFees: positiveOrZero(financials?.brokerageFlatFees),
    flatCommission: positiveOrZero(financials?.flatCommissionAmount),
    runningSplitTotal: positiveOrZero(financials?.runningSplitTotal),
    runningAdditionalChargeTotal: positiveOrZero(
      financials?.runningAdditionalChargeTotal,
    ),
  };
}

/**
 * The brokerage anniversary that applies to a date: the latest one on or before it.
 * Same rule as the backend's GetActiveBrokerageAnniversaryForDateAsync.
 */
export function findAnniversaryForDate(
  anniversaries: IBrokerageAnniversaryModel[] | undefined,
  date: Dayjs | string | null | undefined,
): IBrokerageAnniversaryModel | undefined {
  if (!anniversaries?.length || !date) return undefined;
  const day = dayjs(date).startOf("day");
  let match: IBrokerageAnniversaryModel | undefined;
  for (const anniversary of anniversaries) {
    if (!anniversary.anniversaryDate) continue;
    const anniversaryDay = dayjs(anniversary.anniversaryDate).startOf("day");
    if (anniversaryDay.isAfter(day)) continue;
    if (
      !match ||
      anniversaryDay.isAfter(dayjs(match.anniversaryDate).startOf("day"))
    ) {
      match = anniversary;
    }
  }
  return match;
}
